import { TEnumName, useEnum } from "@/api/services/global/enum";
import { useFormField } from "@/components/elements/FormField/FormField";
import { background, black, blue, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { TOption, TRequiredOne } from "@/types/common";
import { Box, CircularProgress, FormHelperText, Grid, Typography, css, styled } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { FC, useMemo } from "react";
import { typography } from "@/theme/typography";

export type TSelectColorSize = "md" | "lg";

export type TSelectColorProps = {
  size?: TSelectColorSize;
  readonly?: boolean;
  previousValue?: string | number;
  helperText?: string;
  nonControl?: boolean;
  loading?: boolean;
  onClickCloseIcon?: () => void;
  optionColors?: {
    [key: string]: {
      bgColor: string;
      color: string;
    };
  };
  textSelected?: string;
  onChange?: (value: string | number | null) => void;
  value?: string | number | null;
  disabled?: boolean;
  error?: boolean;
} & TRequiredOne<{ options: TOption[]; enumName: TEnumName }>;

export const SelectColor: FC<TSelectColorProps> = ({
  readonly = false,
  previousValue = false,
  nonControl = false,
  helperText,
  options,
  enumName,
  loading = false,
  disabled,
  optionColors,
  textSelected,
  onChange: onChangeProp,
  value: valueProp,
  error: errorProp,
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const { data, isLoading } = useEnum(enumName);
  const value = field ? field.value : valueProp;
  const error = errorProp ?? Boolean(fieldState?.error);

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && value === previousValue;
  }, [fieldState?.isDirty, previousValue, value]);

  const selectOptions = useMemo(() => options ?? data ?? [], [options, data]);

  const handleColorClick = (optionValue: string | number) => {
    if (readonly || disabled) return;

    const newValue = value === optionValue ? null : optionValue;

    if (field) {
      field.onChange(newValue);
      field.onBlur();
    } else {
      onChangeProp?.(newValue);
    }
  };

  const selectedColorOption = useMemo(() => {
    if (!value) return null;
    return optionColors?.[String(value)];
  }, [value, optionColors]);

  return (
    <Box position="relative">
      <ColorGridContainer error={error} readonly={readonly} previous={isPrevious}>
        <Grid container spacing={2}>
          {selectOptions.map((option) => {
            const isDisable = "hidden" in option && option.hidden === true;
            const isHidden = isDisable && value !== option.value;
            if (isHidden) return null;

            const colorOption = optionColors?.[String(option.value)];
            const isSelected = value === option.value;
            const bgColor = colorOption?.bgColor ?? "transparent";

            return (
              <Grid item key={option.value}>
                <ColorBox
                  onClick={() => !isDisable && handleColorClick(option.value)}
                  disabled={Boolean(isDisable || readonly || disabled)}
                  selected={isSelected}
                  bgColor={bgColor}
                >
                  {isSelected && (
                    <CheckIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: text.secondary,
                        fontSize: 24,
                        fontWeight: "bold",
                      }}
                    />
                  )}
                </ColorBox>
              </Grid>
            );
          })}
        </Grid>
      </ColorGridContainer>

      {(isLoading || loading) && (
        <LoadingWrap>
          <CircularProgress color="inherit" size={14} />
        </LoadingWrap>
      )}

      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}

      {textSelected && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="cap12" color={text.secondary}>
            プレビュー：{" "}
          </Typography>
          <PreviewBox
            bgColor={selectedColorOption?.bgColor ?? ""}
            sx={{
              bgcolor: selectedColorOption?.bgColor ?? "",
              color: text.primary,
              px: "12px",
              py: "2px",
              borderRadius: rounded.md,
              display: "inline-flex",
              alignItems: "center",
              boxShadow: selectedColorOption?.bgColor === "#FFFFFF" ? `inset 0 0 0 1px ${black[95]}` : "none",
              outlineOffset: -1,
              ...typography.cap12,
            }}
          >
            {textSelected}
          </PreviewBox>
        </Box>
      )}
    </Box>
  );
};

const ColorGridContainer = styled(Box, {
  shouldForwardProp: (propName: string) => !["error", "readonly", "previous"].includes(propName),
})<{ error: boolean; readonly: boolean; previous: boolean }>`
  width: 100%;
  padding: 8px 0px;
  background: ${background.white};

  ${({ readonly }) =>
    readonly &&
    css`
      background: ${background.disable};
      pointer-events: none;
      border-color: ${divider.middle} !important;
    `}
`;

const ColorBox = styled(Box, {
  shouldForwardProp: (propName: string) => !["selected", "bgColor"].includes(propName),
})<{ selected: boolean; bgColor: string; disabled: boolean }>`
  position: relative;
  width: 32px;
  height: 32px;
  background-color: ${({ bgColor }) => bgColor};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  border: ${({ bgColor }) => (bgColor === "#FFFFFF" || bgColor === "#ffffff" ? `1px solid ${black[95]}` : "none")};
  border-radius: ${rounded.xs};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.2s ease;
  box-sizing: border-box;

  ${({ selected }) =>
    selected &&
    css`
      height: 32px;
      width: 32px;
      border: 2px solid ${text.secondary};
    `}

  &:hover {
    ${({ disabled, selected }) =>
      !disabled &&
      !selected &&
      css`
        border: 1px solid ${blue[70]};
      `}
  }
`;

const PreviewBox = styled(Box)<{ bgColor: string }>`
  /* Styles are applied via sx prop */
`;

const LoadingWrap = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  right: 0;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 30px;
`;
